// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import { CustomAppState } from "@conklin/App/Store/Reducers";
import IconLinkCaretDown from "@conklin/XDL/Icons/IconLinkCaretDown";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, hideElement, mergeWithCss, xClassNames, xScreen } from "@conklin/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, MouseEventHandler, ReactElement, useState } from "react";
import { css } from "styled-components";

interface HeaderClickableProps {
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    titleProps?: TypographyProps;
    title?: string;
    isOpen?: boolean;
    showArrow?: boolean;
    extendedStyles?: HeaderClickableStyle;
    badge?: ReactElement;
}

type Props = HeaderClickableProps & ClickableProps;

export interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
}

export const headerClickableStyles: HeaderClickableStyle = {
    clickable: {
        css: css`
            position: relative;
            border: 1px solid ${color("common.border")};
            border-radius: 1px;
            padding-right: 8px;
            height: 36px;
            cursor: pointer;
            display: flex;
            flex-wrap: nowrap;
            align-items: center;

            &:hover,
            &.opened {
                border-color: ${color("common.ui6")};
                background-color: ${color("common.ui2")};

                .arrow-icon {
                    transform: rotate(180deg);
                    &,
                    & svg {
                        color: ${color("common.ui6")};
                    }
                }
                ${xScreen("lte", "md")} {
                    border-color: transparent;
                    background-color: transparent;
                }
            }

            &:focus {
                outline: none;
            }

            .arrow-icon {
                margin-left: 4px;
                &,
                & svg {
                    height: auto;
                }
            }

            ${xScreen("lte", "md")} {
                padding-right: 0;
                border: none;

                .arrow-icon {
                    display: none;
                }
            }
        `,
    },
    title: {
        size: 14,
        color: "text",
        weight: fontConfig.weight.bold,
        transform: "capitalize",
        css: css`
            letter-spacing: 0;
            line-height: 17px;
            display: block;
            max-width: 130px;
            margin-left: 8px;
            white-space: nowrap;
            &:not(.break-words) {
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .break-words {
                white-space: pre-wrap;
            }

            ${xScreen("lte", "md")} {
                ${hideElement}
            }
        `,
    },
    icon: {
        color: "common.ui1",
        css: css`
            background-color: ${color("primary")};
            height: 100%;
            width: 30px;
            display: flex;
            align-items: center;
            justify-content: center;

            svg {
                height: auto;
                width: 14px;
            }

            ${xScreen("lte", "md")} {
                background-color: transparent;
                &,
                svg {
                    color: ${color("common.ui5")};
                    height: 22px;
                    width: auto;
                }
            }
        `,
    },
};

const HeaderClickable: FC<Props> = ({
    iconProps,
    titleProps,
    title,
    extendedStyles,
    isOpen,
    badge,
    showArrow = true,
    ...otherProps
}) => {
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));
    const classNames = xClassNames.append([isTabletScreen && "mobile-mode", isOpen && "opened"], otherProps.className);
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));

    return (
        <Clickable {...styles.clickable} className={classNames} {...otherProps}>
            <VisuallyHidden>{title}</VisuallyHidden>
            {iconProps && <Icon {...styles.icon} {...iconProps} className="menu-icon" />}
            {badge}
            {title && (
                <Typography {...styles.title} {...titleProps}>
                    {title}
                </Typography>
            )}
            {showArrow && <Icon src={IconLinkCaretDown} size={9} color="common.ui4" className="arrow-icon" />}
        </Clickable>
    );
};

export default HeaderClickable;
